import type { GrammarDifficulty } from '@/content/grammar/schema'
import { MONETIZATION_ENABLED } from '@/monetization-switch'
import type { LearningContentState } from './learning-state'
import {
  getStoryProgressKind,
  type StoryProgressKind
} from './content-progress-core'

export const PATH_VERSION = 'grammar-order-v1'

export type PathNodeType = 'grammar' | 'story_practice' | 'review'
export type PathNodeState = 'current' | 'done' | 'started' | 'upcoming'
export type PathCompletionKind = 'new' | 'started' | 'done'

export type PathGrammarItem = {
  id: string
  order: number
  title: string
  shortTitle?: string
  summary?: string
  href?: string
  form?: string
  difficulty?: GrammarDifficulty
}

export type PathStoryItem = {
  slug: string
  title: string
  simplifiedTitle: string
  level: string
  curriculumConceptId?: string
  isPremiumChapter?: boolean
  wordCount?: number
  grammarConceptIds: string[]
  coverImage?: {
    src: string
    alt: string
  }
}

export type PathNode = {
  id: string
  type: PathNodeType
  title: string
  subtitle?: string
  href: string
  order: number
  sectionKey: string
  reason?: string
  grammarConceptIds: string[]
  storySlug?: string
  grammarConceptId?: string
  story?: PathStoryItem
  difficulty?: GrammarDifficulty
  level?: string
  wordCount?: number
  reviewCount?: number
  optional?: boolean
}

export type ResolvedPathNode = PathNode & {
  completion: PathCompletionKind
  state: PathNodeState
  progress: number | null
}

export type PathSection = {
  id: string
  title: string
  subtitle: string
  volume: number
  lesson: number
  part: number
  order: number
  nodes: PathNode[]
}

export type ResolvedPathSection = Omit<PathSection, 'nodes'> & {
  nodes: ResolvedPathNode[]
  completedCount: number
  startedCount: number
  totalCount: number
  isCurrent: boolean
}

export type PathSummary = {
  sections: ResolvedPathSection[]
  currentNode: ResolvedPathNode | null
  completedCount: number
  totalCount: number
  progressRatio: number
}

export function parseGrammarOrder(order: number) {
  const safeOrder = Number.isFinite(order) ? Math.max(0, Math.trunc(order)) : 0
  return {
    volume: Math.floor(safeOrder / 100000),
    lesson: Math.floor((safeOrder % 100000) / 1000),
    item: Math.floor((safeOrder % 1000) / 10)
  }
}

function chunk<T>(items: T[], size: number) {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

function levelRank(level: string | undefined) {
  if (!level) return 999
  const match = level.match(/^(hsk|tocfl)-(\d+)$/)
  if (!match) return 999
  const trackOffset = match[1] === 'hsk' ? 0 : 20
  return trackOffset + Number(match[2])
}

function findPracticeStories({
  concepts,
  stories,
  usedStorySlugs,
  limit
}: {
  concepts: PathGrammarItem[]
  stories: PathStoryItem[]
  usedStorySlugs: Set<string>
  limit: number
}) {
  const conceptIds = new Set(concepts.map((concept) => concept.id))

  return stories
    .map((story) => {
      const overlapIds = story.grammarConceptIds.filter((id) =>
        conceptIds.has(id)
      )

      return {
        story,
        overlapIds,
        overlap: overlapIds.length,
        alreadyUsed: usedStorySlugs.has(story.slug),
        explicitlyPlaced: story.curriculumConceptId
          ? conceptIds.has(story.curriculumConceptId)
          : false
      }
    })
    .filter(
      (item) => item.explicitlyPlaced && item.overlap > 0 && !item.alreadyUsed
    )
    .sort(
      (a, b) =>
        b.overlap - a.overlap ||
        levelRank(a.story.level) - levelRank(b.story.level) ||
        (a.story.wordCount ?? 9999) - (b.story.wordCount ?? 9999) ||
        a.story.title.localeCompare(b.story.title)
    )
    .slice(0, Math.max(0, limit))
}

export function buildPathSections({
  grammarItems,
  stories,
  dueReviewCount = 0
}: {
  grammarItems: PathGrammarItem[]
  stories: PathStoryItem[]
  dueReviewCount?: number
}): PathSection[] {
  const groups = new Map<string, PathGrammarItem[]>()
  const sortedGrammar = [...grammarItems].sort(
    (a, b) => a.order - b.order || a.id.localeCompare(b.id)
  )

  for (const concept of sortedGrammar) {
    const { volume, lesson } = parseGrammarOrder(concept.order)
    const key = `${volume}:${lesson}`
    groups.set(key, [...(groups.get(key) ?? []), concept])
  }

  const usedStorySlugs = new Set<string>()
  const sections: PathSection[] = []

  Array.from(groups.entries())
    .sort(([a], [b]) => {
      const [aVolume, aLesson] = a.split(':').map(Number)
      const [bVolume, bLesson] = b.split(':').map(Number)
      return aVolume - bVolume || aLesson - bLesson
    })
    .forEach(([key, concepts]) => {
      const [volume, lesson] = key.split(':').map(Number)

      chunk(concepts, 4).forEach((conceptChunk, chunkIndex) => {
        const part = chunkIndex + 1
        const sectionKey = `${volume}:${lesson}:${part}`
        const sectionOrder = conceptChunk[0]?.order ?? 0
        const nodes: PathNode[] = conceptChunk.map((concept) => ({
          id: `grammar:${concept.id}`,
          type: 'grammar',
          title: concept.title,
          subtitle: concept.summary,
          href: concept.href ?? `/grammar/${concept.id}`,
          order: concept.order,
          sectionKey,
          grammarConceptIds: [concept.id],
          grammarConceptId: concept.id,
          reason: concept.shortTitle
            ? `Learn ${concept.shortTitle}`
            : 'Learn the pattern',
          difficulty: concept.difficulty
        }))

        const practiceStories = findPracticeStories({
          concepts: conceptChunk,
          stories,
          usedStorySlugs,
          limit: 4
        })

        for (const item of practiceStories) {
          usedStorySlugs.add(item.story.slug)
          nodes.push({
            id: `story:${item.story.slug}:${sectionKey}`,
            type: 'story_practice',
            title: item.story.simplifiedTitle,
            subtitle: item.story.title,
            href: `/read/${item.story.slug}`,
            order: sectionOrder + 500 + nodes.length,
            sectionKey,
            grammarConceptIds: item.overlapIds,
            storySlug: item.story.slug,
            story: item.story,
            level: item.story.level,
            wordCount: item.story.wordCount,
            optional:
              MONETIZATION_ENABLED && Boolean(item.story.isPremiumChapter),
            reason:
              item.overlapIds.length === 1
                ? `Practices ${item.overlapIds[0]}`
                : `Practices ${item.overlapIds.length} patterns`
          })
        }

        sections.push({
          id: `path-section:${sectionKey}`,
          title: `Volume ${volume}, Lesson ${lesson}`,
          subtitle:
            part > 1
              ? `Part ${part}: ${conceptChunk.length} patterns`
              : `${conceptChunk.length} patterns`,
          volume,
          lesson,
          part,
          order: sectionOrder,
          nodes
        })
      })
    })

  const firstSection = sections[0]
  const reviewCount = Math.max(0, dueReviewCount)
  if (firstSection && reviewCount > 0) {
    firstSection.nodes.unshift({
      id: 'review:due',
      type: 'review',
      title: 'Review due cards',
      subtitle: `${dueReviewCount} due cards`,
      href: '/collections?id=due_today',
      order: firstSection.order - 1,
      sectionKey: firstSection.id,
      grammarConceptIds: [],
      reviewCount
    })
  }

  return sections
}

function grammarCompletion(state: LearningContentState | undefined | null) {
  if (state?.status === 'learned' || state?.status === 'completed') {
    return 'done' satisfies PathCompletionKind
  }
  if (state?.status && state.status !== 'new') {
    return 'started' satisfies PathCompletionKind
  }
  return 'new' satisfies PathCompletionKind
}

function storyCompletion(state: LearningContentState | undefined | null): {
  completion: PathCompletionKind
  progress: number | null
  storyKind: StoryProgressKind
} {
  const storyKind = getStoryProgressKind(state)
  if (storyKind === 'read') {
    return { completion: 'done', progress: 1, storyKind }
  }
  if (storyKind === 'started') {
    return {
      completion: 'started',
      progress: state?.progress ?? null,
      storyKind
    }
  }
  return { completion: 'new', progress: null, storyKind }
}

function initialCompletion(
  node: PathNode,
  learningStates: Map<string, LearningContentState>,
  dueReviewCount: number
) {
  if (node.type === 'grammar') {
    const state = learningStates.get(`grammar:${node.grammarConceptId}`)
    return {
      completion: grammarCompletion(state),
      progress: state?.progress ?? null
    }
  }

  if (node.type === 'story_practice') {
    const state = node.storySlug
      ? learningStates.get(`story:${node.storySlug}`)
      : null
    return storyCompletion(state)
  }

  return {
    completion: dueReviewCount > 0 ? ('started' as const) : ('done' as const),
    progress: dueReviewCount > 0 ? 0 : 1
  }
}

export function resolvePathSections({
  sections,
  learningStates = new Map(),
  dueReviewCount = 0,
  startNodeId = null
}: {
  sections: PathSection[]
  learningStates?: Map<string, LearningContentState>
  dueReviewCount?: number
  startNodeId?: string | null
}): PathSummary {
  const flatNodes = sections.flatMap((section) => section.nodes)
  const startIndex = startNodeId
    ? Math.max(
        0,
        flatNodes.findIndex((node) => node.id === startNodeId)
      )
    : 0
  const completions = new Map(
    flatNodes.map((node) => [
      node.id,
      initialCompletion(node, learningStates, dueReviewCount)
    ])
  )
  const currentNodeId =
    flatNodes
      .slice(startIndex)
      .find(
        (node) =>
          !node.optional && completions.get(node.id)?.completion !== 'done'
      )?.id ?? null

  let completedCount = 0
  let totalCount = 0
  let currentNode: ResolvedPathNode | null = null

  const resolvedSections = sections.map((section) => {
    const nodes = section.nodes.map((node) => {
      const completion = (completions.get(node.id)?.completion ??
        'new') as PathCompletionKind
      const progress = completions.get(node.id)?.progress ?? null
      if (node.type !== 'review' && !node.optional) {
        totalCount += 1
        if (completion === 'done') completedCount += 1
      }

      const resolved: ResolvedPathNode = {
        ...node,
        completion,
        progress,
        state:
          completion === 'done'
            ? 'done'
            : node.id === currentNodeId
              ? 'current'
              : completion === 'started'
                ? 'started'
                : 'upcoming'
      }

      if (resolved.state === 'current') currentNode = resolved
      return resolved
    })
    const meaningfulNodes = nodes.filter(
      (node) => node.type !== 'review' && !node.optional
    )
    const sectionCompletedCount = meaningfulNodes.filter(
      (node) => node.completion === 'done'
    ).length
    const sectionStartedCount = meaningfulNodes.filter(
      (node) => node.completion === 'started'
    ).length

    return {
      ...section,
      nodes,
      completedCount: sectionCompletedCount,
      startedCount: sectionStartedCount,
      totalCount: meaningfulNodes.length,
      isCurrent: nodes.some((node) => node.state === 'current')
    }
  })

  return {
    sections: resolvedSections,
    currentNode,
    completedCount,
    totalCount,
    progressRatio: totalCount > 0 ? completedCount / totalCount : 0
  }
}
